import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { UserPlus, Copy, ArrowRight, Award } from "lucide-react";

const STATUS_COLORS = {
  pending: "bg-slate-100 text-slate-700",
  invited: "bg-[#EAF0FF] text-[#2D6BFF]",
  submitted: "bg-amber-100 text-amber-700",
  approved: "bg-[#E9F8F1] text-[#1F9D6A]",
  rejected: "bg-red-100 text-red-700",
};

export default function ManagerReferrals() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [created, setCreated] = useState(null);
  const [form, setForm] = useState({ referee_name: "", referee_email: "", referee_role_target: "", note: "" });

  const refresh = () => api.get("/referrals/me").then(r => setItems(r.data || [])).catch(() => {});
  useEffect(() => { refresh(); }, []);

  const create = async () => {
    try {
      const r = await api.post("/referrals/", form);
      setCreated(r.data);
      setForm({ referee_name: "", referee_email: "", referee_role_target: "", note: "" });
      toast.success("Referral creato");
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const copyLink = (link) => {
    const fullLink = `${window.location.origin}${link}`;
    navigator.clipboard.writeText(fullLink);
    toast.success(t("referrals.link_copied"));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">{t("referrals.title")}</h1>
          <p className="text-sm text-slate-600">{t("referrals.subtitle")}</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#2D6BFF] text-white" data-testid="new-referral-btn"><UserPlus size={14} className="mr-1" /> {t("referrals.invite_new")}</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t("referrals.invite_new")}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>{t("referrals.referee_name")}</Label><Input value={form.referee_name} onChange={e => setForm({ ...form, referee_name: e.target.value })} data-testid="referee-name" /></div>
              <div><Label>{t("referrals.referee_email")}</Label><Input type="email" value={form.referee_email} onChange={e => setForm({ ...form, referee_email: e.target.value })} data-testid="referee-email" /></div>
              <div><Label>{t("referrals.referee_role")}</Label><Input value={form.referee_role_target} onChange={e => setForm({ ...form, referee_role_target: e.target.value })} placeholder="Fractional COO" /></div>
              <div><Label>{t("referrals.note")}</Label><Textarea rows={2} value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} /></div>
            </div>
            <DialogFooter><Button onClick={create} disabled={!form.referee_name || !form.referee_email} className="bg-[#2D6BFF] text-white">{t("referrals.send_invite")}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {created && (
        <div className="cxo-card p-5 mb-4 bg-[#E9F8F1] border border-[#1F9D6A]/30">
          <p className="font-semibold text-slate-900 flex items-center gap-2"><Award size={18} className="text-[#1F9D6A]" /> Referral creato!</p>
          <p className="text-xs text-slate-600 mt-1">Condividi questo link di invito con il manager:</p>
          <div className="mt-2 flex gap-2">
            <Input readOnly value={`${window.location.origin}${created.invite_link}`} className="font-mono text-xs" />
            <Button size="sm" onClick={() => copyLink(created.invite_link)}><Copy size={14} className="mr-1" /> {t("referrals.copy_link")}</Button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {items.map(r => (
          <div key={r.id} className="cxo-card p-4 flex items-center gap-3">
            <div className="flex-1">
              <p className="font-semibold text-slate-900">{r.referee_name}</p>
              <p className="text-xs text-slate-500">{r.referee_email} · {r.referee_role_target || "—"}</p>
              {r.note && <p className="text-xs text-slate-600 mt-1 italic">“{r.note}”</p>}
            </div>
            <Badge className={`border-0 ${STATUS_COLORS[r.status] || ""}`}>{t(`referrals.status_${r.status}`)}</Badge>
            <Button size="sm" variant="ghost" onClick={() => copyLink(`/register?ref=${r.token}`)}><Copy size={14} /></Button>
          </div>
        ))}
        {!items.length && <div className="cxo-card p-10 text-center text-slate-500">{t("referrals.no_referrals")}</div>}
      </div>
    </div>
  );
}
